import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, MapPin, Building2, Home, Trash2, Pencil, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useDebouncedValue } from "@/lib/hooks";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PropertyDialog from "@/components/PropertyDialog";
import ShareProjectDialog from "@/components/ShareProjectDialog";

export default function PropertiesPage() {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const qc = useQueryClient();

  const locationsQ = useQuery({
    queryKey: ["project-locations"],
    queryFn: async () => (await api.get("/properties/locations")).data.items,
    staleTime: 60_000,
  });

  const listQ = useQuery({
    queryKey: ["properties", { search: debounced }],
    queryFn: async () => {
      const params = { limit: 300 };
      if (debounced) params.search = debounced;
      const { data } = await api.get("/properties", { params });
      return data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/properties/${id}`),
    onSuccess: () => {
      toast.success("Project deleted");
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["project-locations"] });
    },
    onError: () => toast.error("Delete failed"),
  });

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setDialogOpen(true);
  };

  const items = listQ.data?.items || [];

  const allLocations = locationsQ.data || [];
  const q = search.trim().toLowerCase();
  const locationMatches = q
    ? allLocations.filter((l) => l.toLowerCase().includes(q)).slice(0, 8)
    : [];

  const pickLocation = (loc) => {
    setSearch(loc);
    setSuggestOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy lg:text-3xl">Projects</h2>
        <Button
          data-testid="project-add-btn"
          onClick={openNew}
          className="h-11 rounded-full bg-navy px-4 text-sm font-semibold hover:brightness-110"
        >
          <Plus className="h-4 w-4" /> Add Project
        </Button>
      </div>

      <div className="relative" onBlur={(e) => {
        // Close the dropdown only when focus leaves the wrapper entirely.
        if (!e.currentTarget.contains(e.relatedTarget)) setSuggestOpen(false);
      }}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="project-search"
          placeholder="Search project, developer, location…"
          className="h-12 rounded-xl border-slate-200 bg-white pl-10 text-[15px] shadow-sm"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSuggestOpen(true); }}
          onFocus={() => setSuggestOpen(true)}
          autoComplete="off"
        />

        {suggestOpen && locationMatches.length > 0 && (
          <div
            data-testid="project-location-suggestions"
            className="absolute left-0 right-0 top-[52px] z-20 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
          >
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Locations
            </div>
            {locationMatches.map((loc) => (
              <button
                type="button"
                key={loc}
                data-testid={`project-location-suggest-${loc.replace(/\W+/g, "-").toLowerCase()}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickLocation(loc)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                <span className="flex-1 truncate">{loc}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {listQ.isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="soft-card h-32 animate-pulse bg-slate-50" />
          ))}

        {!listQ.isLoading && items.length === 0 && (
          <div className="soft-card p-8 text-center text-slate-500" data-testid="project-empty">
            No projects yet. Tap <b>Add Project</b> to create one.
          </div>
        )}

        {items.map((p) => (
          <PropertyCard
            key={p.id}
            prop={p}
            onEdit={() => openEdit(p)}
            onDelete={() => {
              if (window.confirm(`Delete ${p.project_name}?`)) deleteMut.mutate(p.id);
            }}
            onShare={() => setShareTarget(p)}
          />
        ))}
      </div>

      <PropertyDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        property={editing}
      />

      <ShareProjectDialog
        project={shareTarget}
        open={Boolean(shareTarget)}
        onOpenChange={(v) => { if (!v) setShareTarget(null); }}
      />
    </div>
  );
}

function PropertyCard({ prop, onEdit, onDelete, onShare }) {
  const configSummary = (prop.configurations || [])
    .map((c) => c.bhk)
    .filter(Boolean)
    .join(" · ");

  const stats = [
    prop.total_towers ? { label: "Towers", value: prop.total_towers } : null,
    prop.total_floors ? { label: "Floors", value: prop.total_floors } : null,
    prop.total_land_parcel ? { label: "Land", value: prop.total_land_parcel } : null,
  ].filter(Boolean);

  return (
    <div className="soft-card p-4 md:p-5" data-testid={`project-card-${prop.id}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        {/* Left — identity */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--brand-gold))]" />
            <h3
              className="text-lg font-bold leading-tight text-navy break-words"
              data-testid={`project-card-name-${prop.id}`}
            >
              {prop.project_name}
            </h3>
          </div>
          <div className="mt-1 text-sm text-slate-600">{prop.developer_name}</div>
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
            <MapPin className="h-3.5 w-3.5" />
            {prop.location || "—"}
          </div>

          {configSummary && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-700">
              <Home className="h-3 w-3" /> {configSummary}
            </div>
          )}

          {prop.rera_possession && (
            <div className="mt-2 text-[11px]">
              <span className="font-semibold text-slate-600">RERA / Possession:</span>{" "}
              <span className="text-slate-700">{prop.rera_possession}</span>
            </div>
          )}
        </div>

        {/* Middle — stats + configurations */}
        <div className="min-w-0 md:w-[280px] lg:w-[320px] md:shrink-0">
          {stats.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg bg-slate-50 px-2 py-1.5 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{s.label}</div>
                  <div className="text-sm font-bold text-navy">{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {(prop.configurations || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {prop.configurations.map((c) => (
                <div key={c.bhk} className="rounded-lg border border-slate-100 p-2 text-xs">
                  <div className="font-semibold text-navy">{c.bhk}</div>
                  {(c.variants || []).length === 0 ? (
                    <div className="text-slate-400">No variants</div>
                  ) : (
                    <ul className="mt-0.5 space-y-0.5">
                      {c.variants.map((v, i) => (
                        <li key={i} className="flex justify-between gap-2">
                          <span className="text-slate-600">{v.sqft || "—"} sqft</span>
                          <span className="font-medium text-slate-800">{v.price || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — actions + secondary meta */}
        <div className="md:w-[200px] md:shrink-0 md:text-right">
          <div className="flex items-center gap-1 md:justify-end">
            <button
              data-testid={`project-share-${prop.id}`}
              onClick={onShare}
              className="grid h-9 w-9 place-items-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
              aria-label="Share on WhatsApp"
              title="Share on WhatsApp"
            >
              <MessageCircle className="h-4 w-4" />
            </button>
            <button
              data-testid={`project-edit-${prop.id}`}
              onClick={onEdit}
              className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100"
              aria-label="Edit"
            >
              <Pencil className="h-4 w-4 text-slate-500" />
            </button>
            <button
              data-testid={`project-delete-${prop.id}`}
              onClick={onDelete}
              className="grid h-9 w-9 place-items-center rounded-full hover:bg-rose-50"
              aria-label="Delete"
            >
              <Trash2 className="h-4 w-4 text-rose-500" />
            </button>
          </div>

          {prop.developer_commitment && (
            <div className="mt-3 rounded-lg bg-amber-50/60 p-2 text-[11px] md:text-left">
              <div className="mb-0.5 font-semibold text-amber-800">Developer commitment</div>
              <div className="text-slate-700">{prop.developer_commitment}</div>
            </div>
          )}

          {prop.comments && (
            <div className="mt-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600 md:text-left">
              <span className="font-semibold text-slate-700">Notes:</span> {prop.comments}
            </div>
          )}

          {(prop.amenities || []).length > 0 && (
            <div className="mt-3 text-[11px] text-slate-500 md:text-right">
              <span className="font-semibold text-slate-600">Amenities:</span>{" "}
              {prop.amenities.length} included
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
